import { logError } from '@/lib/sentry.lib';

import { API } from '@repo/hooks';
import { translate } from '@repo/phrase';
import type { CancelOrderReasonCode, OrderAsset } from '@repo/types/order';
import { getNotifier } from '@repo/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { cancelOrder } from '@/api/asset.api';
import { useAssetParams } from '@/hooks/asset.hooks';

interface Props {
    orderData: OrderAsset | undefined;
    reasonCode?: CancelOrderReasonCode | undefined;
    closeModal: () => void;
}
export const useCancelOrderMutation = ({ orderData, reasonCode, closeModal }: Props) => {
    const { assetId } = useAssetParams();
    const notifier = getNotifier();
    const queryClient = useQueryClient();

    const cancelOrderMutation = useMutation({
        mutationFn: () =>
            cancelOrder({
                orderId: assetId,
                orderNoSource: orderData?.assetDetails.orderNoSource,
                cashAndCarryOrder: orderData?.assetDetails.cashAndCarryOrder,
                reasonCode: reasonCode,
                buCode: orderData?.assetDetails.buCode,
                orderType: orderData?.assetDetails.orderType,
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [API.assetDetails] });
            closeModal();
            notifier?.add({
                title: translate('dialog_cancel_order_notification_title'),
                message: translate('dialog_cancel_order_notification_body', {
                    number: assetId,
                }),
            });
        },
        onError: (error) => {
            closeModal();
            notifier?.add({
                title: translate('dialog_cancel_order_notification_error_title'),
                message: translate('dialog_cancel_order_notification_error_body'),
                type: 'error',
            });
            logError(error);
        },
    });

    return cancelOrderMutation;
};
